/**
 * The family's paper chore list, ready to load into the app.
 *
 * This is the "Power Chore List" that lives on the fridge, transcribed once so
 * nobody has to type twenty chores on a phone. `owner` is the name written
 * against that section of the poster; the importer matches it to a child by
 * name and falls back to "everyone" when there is no such child, so the list
 * stays useful if the household changes.
 */
export interface StarterChore {
  title: string;
  icon: string;
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  points: number;
  /** Which weekdays a daily chore is due on (1 = Monday). */
  weekdays?: number[];
  /** Which weekday a weekly or biweekly chore is due by. */
  dueWeekday?: number;
  notes?: string;
}

export interface StarterGroup {
  name: string;
  /** Who the poster gives this section to, or null for everyone. */
  owner: string | null;
  chores: StarterChore[];
}

const SCHOOL_DAYS = [1, 2, 3, 4, 5];

export const STARTER_GROUPS: StarterGroup[] = [
  {
    name: 'Everyday super moves',
    owner: null,
    chores: [
      { title: 'Wipe your bathroom sink', icon: '🚿', frequency: 'daily', points: 1, notes: 'If it looks messy.' },
      {
        title: 'Fluff your pillows',
        icon: '🛏️',
        frequency: 'daily',
        points: 1,
        notes: "Bonus points if you make them look like Snorlax's bed!",
      },
      {
        title: 'School bag & jacket in the closet',
        icon: '🎒',
        frequency: 'daily',
        points: 1,
        weekdays: SCHOOL_DAYS,
      },
      { title: 'Do your homework', icon: '✏️', frequency: 'daily', points: 1, weekdays: SCHOOL_DAYS },
    ],
  },
  {
    name: 'Weekly quests',
    owner: null,
    chores: [
      { title: 'Bathroom mission: deep clean', icon: '🛁', frequency: 'weekly', points: 4, dueWeekday: 6 },
      {
        title: 'Tidy your room',
        icon: '🧹',
        frequency: 'weekly',
        points: 3,
        dueWeekday: 5,
        notes: 'Desk clear for homework, no clothes on the floor, closet doors should close.',
      },
      { title: 'Clean your allrum', icon: '🛋️', frequency: 'weekly', points: 3, dueWeekday: 6 },
    ],
  },
  {
    name: 'Pet hero care',
    owner: 'Charlie',
    chores: [
      { title: 'Scoop the catbox', icon: '🐈', frequency: 'daily', points: 1 },
      { title: 'Check food & water', icon: '🥫', frequency: 'daily', points: 1 },
      { title: 'Wash the water bowl', icon: '🧼', frequency: 'biweekly', points: 2, dueWeekday: 7 },
    ],
  },
  {
    name: 'Special missions',
    owner: 'Milly',
    chores: [
      {
        title: 'Make breakfast',
        icon: '🍳',
        frequency: 'biweekly',
        points: 4,
        dueWeekday: 6,
        notes: "Every other Sat/Sun, with daddy's help if needed.",
      },
      { title: 'Sort laundry with Mama', icon: '🧺', frequency: 'weekly', points: 2, dueWeekday: 7 },
      { title: 'Bring in the mail', icon: '📮', frequency: 'daily', points: 1, weekdays: SCHOOL_DAYS },
    ],
  },
  {
    name: 'Joint mission',
    owner: null,
    chores: [{ title: 'Take out recycling', icon: '♻️', frequency: 'weekly', points: 2, dueWeekday: 2 }],
  },
];

export function frequencyWord(chore: StarterChore): string {
  if (chore.frequency === 'daily') {
    return chore.weekdays && chore.weekdays.length < 7 ? 'School days' : 'Every day';
  }
  if (chore.frequency === 'weekly') return 'Weekly';
  if (chore.frequency === 'biweekly') return 'Every 2 weeks';
  return 'Monthly';
}

/**
 * Jobs that come round rather than fall due. The dishwasher runs twice on a
 * Tuesday and not at all on a Wednesday, so it is a rota, not a schedule.
 */
export interface StarterTurnJob {
  title: string;
  icon: string;
  points: number;
  notes?: string;
}

export const STARTER_TURN_JOBS: StarterTurnJob[] = [
  {
    title: 'Empty the dishwasher',
    icon: '🍽️',
    points: 1,
    notes: 'Whoever has done fewest is up next. Doing an extra one puts you ahead.',
  },
];
